import React, { useState } from 'react';
import { 
  IconButton, 
  Menu, 
  MenuItem, 
  Typography, 
  Box,
  Tooltip
} from '@mui/material';
import { LanguageRounded, TranslateRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const languages = [
  {
    code: 'en',
    name: 'English',
    flag: '🇺🇸'
  },
  {
    code: 'tr',
    name: 'Türkçe',
    flag: '🇹🇷'
  }
];

const LanguageSwitcher = ({ 
  variant = 'icon', // 'icon' | 'text' | 'full'
  size = 'medium',
  color = 'inherit'
}) => {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode);
    handleClose();
  };

  const renderButton = () => {
    switch (variant) {
      case 'text':
        return (
          <Tooltip title="Change Language">
            <Box
              component="button"
              onClick={handleClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: 'inherit',
                padding: 1,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <Typography variant="body2">
                {currentLanguage.flag} {currentLanguage.name}
              </Typography>
            </Box>
          </Tooltip>
        );
      
      case 'full':
        return (
          <Tooltip title="Change Language">
            <Box
              component="button"
              onClick={handleClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: '1px solid',
                borderColor: 'divider',
                background: 'transparent',
                cursor: 'pointer',
                color: 'inherit',
                padding: '8px 12px',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <TranslateRounded fontSize="small" />
              <Typography variant="body2">
                {currentLanguage.flag} {currentLanguage.name}
              </Typography>
            </Box>
          </Tooltip>
        );
      
      case 'icon':
      default:
        return (
          <Tooltip title="Change Language">
            <IconButton 
              onClick={handleClick}
              size={size}
              color={color}
            >
              <LanguageRounded />
            </IconButton>
          </Tooltip>
        );
    }
  };

  return (
    <>
      {renderButton()}
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'language-button',
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            selected={language.code === currentLanguage.code}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              minWidth: 120
            }}
          >
            <Typography component="span" sx={{ fontSize: '1.2em' }}>
              {language.flag}
            </Typography>
            <Typography variant="body2">
              {language.name}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSwitcher;